import { FunctionComponent, useState, ComponentType, useEffect } from "react";
import * as Sentry from "@sentry/react";
import AlertIcon from "@icons/alert-triangle.svg?react";
import RefreshIcon from "@icons/refresh-cw-03.svg?react";
import { isRecorderActive, selectRecorderActive } from "@common/lib/recorderActivity";
import { useAppSelector } from "@/common/hooks/useRedux";

interface AsyncComponentProps {
    loadComponent: () => Promise<{ default: React.ComponentType<any> }>;
    tabIndex?: string;
    renderType?: string;
}

// This component is used to lazy load components (pages) asynchronously
// https://reactjs.org/docs/code-splitting.html#reactlazy
const AsyncComponent: FunctionComponent<AsyncComponentProps> = ({ loadComponent, tabIndex, renderType }) => {
    const [Component, setComponent] = useState<ComponentType<any> | null>(null);
    // Per-tab (sessionStorage survives reload) so one tab's successful load can't reset another tab's retry loop.
    const [loadAttempts] = useState(Number(sessionStorage.getItem("loadAttempts")) || 0);
    const [loadError, setLoadError] = useState(false);
    const [retryBlockedByRecording, setRetryBlockedByRecording] = useState(false);
    const recorderBusy = useAppSelector((state) => selectRecorderActive(state.globalState));

    // EN-860: clear the "still recording" hint once the recorder goes idle so it can't linger stale
    // after the recording/upload that blocked the retry has finished (the flag is set imperatively).
    useEffect(() => {
        if (!recorderBusy) setRetryBlockedByRecording(false);
    }, [recorderBusy]);

    const handleRetry = () => {
        // Never tear down an in-flight recording/upload with a reload — keep the error UI visible and
        // tell the user why, so the click doesn't look like a dead button. (EN-837, mirrors EditorLoadingPage)
        if (isRecorderActive()) {
            setRetryBlockedByRecording(true);
            return;
        }
        setLoadError(false);
        sessionStorage.setItem("loadAttempts", String(loadAttempts + 1));
        window.location.reload();
    };

    // "Go to home" is a full-page navigation, so it tears down an in-flight recording exactly like a reload.
    const handleGoHome = (event: React.MouseEvent<HTMLAnchorElement>) => {
        if (isRecorderActive()) {
            event.preventDefault();
            setRetryBlockedByRecording(true);
        }
    };

    useEffect(() => {
        loadComponent()
            .then((module) => {
                // Every deploy renames all chunks, so a tab opened on the previous build fails its next
                // lazy import and recovers via reload. Reset the counter on success so those recoveries
                // don't accumulate across deploys and push a later one straight to the error UI.
                sessionStorage.removeItem("loadAttempts");
                setComponent(() => module.default);
            })
            .catch((error) => {
                // A full-page reload mid-recording tears down the MediaRecorder and loses audio.
                // While a recording/upload is active, surface the manual retry UI instead.
                if (isRecorderActive()) {
                    Sentry.captureException(error, { tags: { boundary: "async-component" } });
                    setLoadError(true);
                    return;
                }
                if (loadAttempts < 3) {
                    sessionStorage.setItem("loadAttempts", String(loadAttempts + 1));
                    window.location.reload();
                } else {
                    sessionStorage.removeItem("loadAttempts"); // Clear after limit reached
                    Sentry.captureException(error, { tags: { boundary: "async-component" } });
                    setLoadError(true);
                }
            });
    }, [loadComponent, loadAttempts]);

    if (loadError) {
        return (
            <div className="flex items-center justify-center h-full min-h-[60vh] p-6">
                <div className="flex flex-col items-center gap-4 max-w-md text-center">
                    <img className="h-16 w-16" src="/favicon.png" alt="" />
                    <div className="flex items-center gap-2 text-max-900">
                        <AlertIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
                        <h1 className="text-xl font-semibold">{`We couldn't load this page`}</h1>
                    </div>
                    <p className="text-sm text-gray-500">
                        HappyDoc may have just been updated, or your connection dropped. Retrying usually fixes it.
                    </p>
                    <div className="flex items-center gap-3">
                        <button onClick={handleRetry} className="primary-button flex items-center gap-1.5">
                            <RefreshIcon className="h-4 w-4" aria-hidden="true" />
                            Retry
                        </button>
                        <a href="/" onClick={handleGoHome} className="secondary-button">
                            Go to home
                        </a>
                    </div>
                    {retryBlockedByRecording && recorderBusy && (
                        <p className="text-xs text-gray-400">
                            {`Finishing your current recording — try again in a moment.`}
                        </p>
                    )}
                </div>
            </div>
        );
    }

    return Component ? <Component tabIndex={tabIndex} renderType={renderType} /> : null;
};

export default AsyncComponent;
